/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TwMap from '../TwMap/TwMap';
import TwMarker, {
  LiveLocationMarker,
  LocationMarker,
  LocationMarkerType
} from '../TwMarker/TwMarker';
import useDirections from '../../hooks/useGoogleDirections';
import useAuth from '../../providers/Auth/Auth.provider';
import useGoogleMaps from '../../providers/GoogleMaps/GoogleMaps.provider';
import { LOAD_TYPE, LoadLocation } from '../../redux/api/load/load.model';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import { getLiveLocationMarker, getMapMarkers, getMarkerIcon } from '../../utils/mapUtils';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { PROVIDER, USER_ROLE } from '../../providers/User/User.model';
import UploadBol from '../../pages/UploadBol/UploadBol';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setEntityOverlayInitialTabId,
  setLoadDetailReload,
  setTripDetailReload
} from '../../redux/features/navigation/navigationSlice';
import { setSelectedTripId } from '../../redux/features/orders/ordersSlice';

import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetEquipmentByIdQuery } from '../../redux/api/equipment/equipmentApi';
import DateUtils from '../../utils/dateUtils';
import TwLiveTrackingAlert, {
  LiveEntityType,
  OrderTripLiveStatus
} from '../TwLiveTrackingAlert/TwLiveTrackingAlert';
import { getLiveStatus, getLiveTrackEntity } from '../../utils/liveTrackingUtils';
import { ClientResponse } from '../../redux/api/customer/customer.model';
import { TabPanel } from '../TwTabPanel/TwTabPanel';
import HeaderOverlay from './HeaderOverlay/HeaderOverlay';
import OverlayTabs from './OverlayTabs/OverlayTabs';
import OrderTab from './TabsContent/OrderTab';

import {
  Trip,
  TRIP_STATUS,
  TripDriverAckStatus,
  TripStop
} from '../../redux/api/trips/trips.model';
import { CLIENT_TYPE, Order, ORDER_STATUS } from '../../redux/api/orders/orders.model';
import '../TwLoadDetail/TwLoadDetail.scss';
import { EntityType } from '../../redux/Global.model';
import TripTab from './TabsContent/TripTab';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import {
  DriverAckType,
  OwnerOperatorDriverAckType,
  TenantDriverSettings
} from '../../redux/api/tenantDriverSettings/tenantDriverSettings.model';
import { DRIVER_TYPE } from '../../redux/api/driver/driver.model';
import { TwDriverAckConfirmationModal } from '../TwDriverAckConfirmationModal/TwDriverAckConfirmationModal';
import { TwRejectedTrip } from '../TwRejectedTrip/TwRejectedTrip';
import { useGetOrderByIdQuery } from '../../redux/api/orders/ordersApi';
import { useGetTripByIdQuery } from '../../redux/api/trips/tripApi';

const DEFAULT_MAP_CENTER = { lat: 39.7567241, lng: -101.6269877 };
const MAX_STOP_MARKER_COUNT = 25;

interface TwEntityOverlayProps {
  entityId?: string;
  entityType?: EntityType;
  open: boolean;
  shareOrderSuccess?: boolean;
  shareTripSuccess?: boolean;
  readonly?: boolean;
  forceOnlyEdit?: boolean;
  onClose?: () => void;
  onTripStatusChange?: () => void;
  onOrderStatusChange?: () => void;
  onSelectedTripChange?: (segmentId: string) => void; // For map marker click?
  onSelectedOrderChange?: (orderId: string) => void; // For map marker click?
  selectedTripId?: string; // autoselect trip tab when overlay opens
  selectedOrderId?: string; // autoselect order tab when overlay opens
}

// Add styled components at the top level
const StyledTabsContainer = styled(Box)({
  '.MuiTabs-root': {
    minHeight: '48px',
    '.MuiTabs-scroller': {
      '.MuiTabs-flexContainer': {
        gap: '8px',
        padding: '0 16px',
        '.MuiTab-root': {
          minWidth: 'unset',
          padding: '8px 16px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '200px',
          minHeight: '36px'
        }
      }
    }
  }
});

const TwEntityOverlay: FC<TwEntityOverlayProps> = ({
  entityId,
  entityType,
  open,
  readonly,
  forceOnlyEdit,
  onClose,
  onOrderStatusChange,
  onTripStatusChange,
  onSelectedTripChange,
  onSelectedOrderChange,
  selectedTripId,
  selectedOrderId
}) => {
  const dispatch = useAppDispatch();
  const { loaded: googleMapsLoaded } = useGoogleMaps();
  const {
    enableDynamicWebTwain,
    enableLiveTracking,
    enableDriverAcknowledge,
    enableHideGoogleMapsAndRoutingCalculations
  } = useFeatureFlags();
  const { addNotification } = useNotifications();
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const mapRef = useRef<HTMLDivElement | null>(null);
  const isRequireBolActive = getTenantSetting('require-bol');
  const isDriverAckSettingOn = getTenantSetting('driver-dispatch-ack');
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const showMapToDrivers = getTenantSetting('show-route-map-to-drivers');

  // Overlay reload
  const loadDetailReload = useAppSelector((state) => state.navigation.loadDetailReload);
  const tripDetailReload = useAppSelector((state) => state.navigation.tripDetailReload);

  const entityOverlayInitialTabId = useAppSelector(
    (state) => state.navigation.entityOverlayInitialTabId
  );

  const [showMap, setShowMap] = useState<boolean>(false);
  const [map, setMap] = useState<google.maps.Map>();
  const [markers, setMarkers] = useState<LocationMarker[]>([]);
  const [primaryClient, setPrimaryClient] = useState<ClientResponse | null>(null);
  const [isTwScannerOpen, setIsTwScannerOpen] = useState<boolean>(false);
  const [uploadBolOpen, setUploadBolOpen] = useState<boolean>(false);

  const [currentTab, setCurrentTab] = useState<number>(0);
  const [stops, setStops] = useState<TripStop[]>([]);
  const [displayOrders, setDisplayOrders] = useState<Order[]>([]); // IF OVERLAY RENDERS TRIP
  const [displayTrips, setDisplayTrips] = useState<Trip[]>([]); // IF OVERLAY RENDERS AN ORDER

  // live tracking states
  const [currentTruckId, setCurrentTruckId] = useState<string | undefined | null>(undefined);
  const [liveMarkers, setLiveMarkers] = useState<LiveLocationMarker[]>([]);
  const [liveStatus, setLiveStatus] = useState<OrderTripLiveStatus>(OrderTripLiveStatus.notStarted);
  const [liveTrackedEntity, setLiveTrackedEntity] = useState(LiveEntityType.order);

  // driver ack
  const [tenantDriverSettings, setTenantDriverSettings] = useState<Pick<
    TenantDriverSettings,
    'company_driver_ack_type' | 'owner_operator_driver_ack_type'
  > | null>(null);
  const [openDriverAckConfirmation, setOpenDriverAckConfirmation] = useState<boolean>(false);
  const [tripIdSelected, setTripIdSelected] = useState<string | null>(null);

  const isOrder = useMemo(() => {
    return entityType === EntityType.ORDER && !!entityId;
  }, [entityType, entityId]);
  const isTrip = useMemo(() => {
    return entityType === EntityType.TRIP && !!entityId;
  }, [entityType, entityId]);

  const isDriver = userData?.role === USER_ROLE.DRIVER;
  const isCarrier = userData?.role === USER_ROLE.CARRIER;
  const isCustomerContact = userData?.role === USER_ROLE.CUSTOMER_CONTACT;
  const isDriverOrCarrier = isDriver || isCarrier;

  useEffect(() => {
    if (!isDriver) setShowMap(true);
    else if (isDriver && showMapToDrivers) {
      setShowMap(true);
    }
  }, [isDriver, showMapToDrivers]);

  useEffect(() => {
    if (!userData || !userData.tenantDriverSettings) return;

    setTenantDriverSettings(userData.tenantDriverSettings);
  }, [userData?.tenantDriverSettings]);

  const { data: truck, refetch: refetchEquipment } = useGetEquipmentByIdQuery(
    currentTruckId ?? skipToken,
    {
      skip: !currentTruckId
    }
  );

  const {
    data: orderData,
    refetch: refetchOrder,
    isFetching: isOrderFetching,
    isError: isOrderError
  } = useGetOrderByIdQuery(
    { orderId: entityId ?? '' },
    {
      skip: entityType !== EntityType.ORDER || !entityId
    }
  );

  const {
    data: tripData,
    refetch: refetchTrip,
    isFetching: isTripFetching,
    isError: isTripError
  } = useGetTripByIdQuery(entityId ?? '', {
    skip: entityType !== EntityType.TRIP || !entityId
  });

  const filterTrips = useCallback(
    (trips: Trip[]): Trip[] => {
      return trips.filter(
        (trip: Trip): boolean =>
          isDriver
            ? trip?.drivers.some((driver) => driver.id === userData?.driver?.id) // filter for driver
            : trip?.carrier?.twuser_id === userData?.id // filter for carrier
      );
    },
    [isDriver, userData?.driver?.id, userData?.id]
  );

  useEffect(() => {
    if (isOrderError || isTripError) {
      addNotification({
        message: `Failed to load ${isOrderError ? 'order' : 'trip'} details, please try again`,
        variant: 'error'
      });
    }
  }, [isOrderError, isTripError]);

  useEffect(() => {
    if (isOrderFetching || isOrderError || !orderData) return;
    if (!enableLtl && entityOverlayInitialTabId) {
      dispatch(setTripDetailReload(true));
    }

    const existingStops =
      orderData.stops && orderData.stops.length > 0
        ? orderData.stops
        : orderData.full_route_stop_overview;

    const stops = existingStops?.map((stop, index) => {
      return { ...stop, sort_order: index + 1 };
    });

    setStops(stops || []);
    setDisplayTrips(isDriverOrCarrier ? filterTrips(orderData.trips) : orderData.trips);
  }, [
    dispatch,
    isOrderFetching,
    orderData,
    enableLtl,
    entityOverlayInitialTabId,
    isOrderError,
    filterTrips,
    isDriverOrCarrier
  ]);

  useEffect(() => {
    if (isTripFetching || isTripError || !tripData) return;

    const stops = tripData.stops?.map((stop, index) => {
      return { ...stop, sort_order: index + 1 };
    });

    setStops(stops || []);
    setDisplayTrips(isDriverOrCarrier ? filterTrips([tripData]) : [tripData]);
    tripData.orders && setDisplayOrders(tripData.orders);
  }, [isTripFetching, tripData, isTripError, filterTrips, isDriverOrCarrier]);

  const usesPcMiler = useMemo((): boolean => {
    return !!displayTrips.find(
      (trip) =>
        trip.loaded_mileage_source === PROVIDER.PC || trip.empty_mileage_source === PROVIDER.PC
    );
  }, [displayTrips]);

  const hasInitialTabChanged = useRef(false);

  useEffect(() => {
    if (open) {
      if (isOrder) {
        refetchOrder();
      } else if (isTrip) {
        refetchTrip();
      }
    }
  }, [open]);

  useEffect(() => {
    if (!hasInitialTabChanged.current) {
      if (isTrip && displayOrders && displayOrders.length > 0) {
        if (isDriver && enableLtl) {
          // this isDriver and enableLtl is a quick fix because the entityOverlayInitialTabId is not set from active trips (not sure if that's a bug) and I couldn't find an easy way to find the right index otherwise so I'm assuming we'd show the trip at the end for driver and LTL based on what's in the overlaytabs
          setCurrentTab(displayOrders.length);
          hasInitialTabChanged.current = true;
        } else {
          const orderIndex = displayOrders.findIndex(
            (order) => order.id === entityOverlayInitialTabId
          );
          if (orderIndex >= 0) {
            setCurrentTab(orderIndex + 1);
            hasInitialTabChanged.current = true;
          }
        }
        // find index of the selected order
      } else if (isOrder && displayTrips && displayTrips.length > 0) {
        // find index of the selected trip
        const tripIndex = displayTrips.findIndex((trip) => trip.id === entityOverlayInitialTabId);
        if (tripIndex >= 0) {
          setCurrentTab(tripIndex + 1);
          hasInitialTabChanged.current = true;
        }
      }
    }
  }, [entityOverlayInitialTabId, displayOrders, displayTrips]);

  // LIVE TRACKING IF OVERLAY RENDERS AN ORDER
  useEffect(() => {
    if (!orderData) return;

    if (isDriverOrCarrier || !enableLiveTracking) return;

    if (orderData && orderData.order_type === LOAD_TYPE.SIMPLE) {
      setCurrentTruckId(orderData?.trips[0]?.truck_id);
    } else if (currentTab === 0) {
      // select truck from segment that is in transit for full route tab
      const segmentInTransit = displayTrips?.find(
        (segment) => segment.status === TRIP_STATUS.IN_TRANSIT
      );
      setCurrentTruckId(segmentInTransit?.truck_id);
    } else if (displayTrips && displayTrips.length > 0) {
      // select truck from current segment for segment tab
      const currentSegment = displayTrips[currentTab - 1];
      setCurrentTruckId(currentSegment?.truck_id);
    }
    truck && refetchEquipment();
    setLiveStatus(getLiveStatus(orderData, currentTab, displayTrips));
    setLiveTrackedEntity(getLiveTrackEntity(currentTab));
  }, [currentTab, orderData]);

  // LIVE TRACKING IF OVERLAY RENDERS AN ORDER
  useEffect(() => {
    if (!orderData) return;

    if (isDriverOrCarrier || !enableLiveTracking) return;
    setLiveMarkers([]);
    const truckHasTrackingInfo =
      truck?.asset_eld_id &&
      truck?.eld_connection_id &&
      truck?.latest_reading?.latitude &&
      truck?.latest_reading?.longitude;
    if (!truckHasTrackingInfo) return;

    let showLiveTracking = false;

    if (orderData?.order_type === LOAD_TYPE.SIMPLE) {
      // for simple load check load status
      if (orderData?.status === ORDER_STATUS.IN_TRANSIT) showLiveTracking = true;
    } else {
      const isFullRouteTab = currentTab === 0;
      if (isFullRouteTab) {
        // for full route tab check if any segment is in transit
        const segmentInTransit = displayTrips?.find(
          (segment) => segment.status === TRIP_STATUS.IN_TRANSIT
        );
        if (segmentInTransit) {
          showLiveTracking = true;
        }
      } else if (!isFullRouteTab) {
        // for segment tab check if segment is in transit
        if (
          displayTrips &&
          displayTrips.length > 0 &&
          displayTrips[currentTab - 1]?.status === TRIP_STATUS.IN_TRANSIT
        ) {
          showLiveTracking = true;
        }
      }
    }

    showLiveTracking &&
      truck?.latest_reading?.latitude &&
      truck?.latest_reading?.longitude &&
      setLiveMarkers([
        getLiveLocationMarker(
          parseFloat(truck?.latest_reading?.latitude),
          parseFloat(truck?.latest_reading?.longitude),
          DateUtils.getFullLocaleDate(truck?.latest_reading?.truckercloud_reading_timestamp),
          DateUtils.getFullLocaleDate(truck?.latest_reading?.last_attempted_reading_timestamp)
        )
      ]);
  }, [truck, orderData, currentTab]);

  const { getAndRenderRoute } = useDirections({
    map: map,
    renderOnMap: true,
    renderOptions: {
      polylineOptions: {
        strokeColor: '#7E858E',
        strokeOpacity: 0.5,
        strokeWeight: 4
      },
      suppressMarkers: true
    }
  });

  // Lazy load the getAndRenderRoute function to avoid unnecessary calculations
  const lazyGetAndRenderRoute = useCallback(
    async (directionsRequest: google.maps.DirectionsRequest, usePcMiler: boolean) => {
      if (!getAndRenderRoute) return;

      try {
        // Only render the route if we have displayTrips data
        if (displayTrips && displayTrips.length > 0) {
          await getAndRenderRoute(directionsRequest, usePcMiler);
        }
        // If no displayTrips, skip rendering the route entirely
      } catch (error) {
        console.error('DIRECTIONS RESULT ERROR', error);
        map?.setZoom(3);
      }
    },
    [getAndRenderRoute, displayTrips, map]
  );

  // CUSTOMER INFO FOR ORDER OVERLAY
  useEffect(() => {
    if (!open || !orderData) return;
    const primaryClient =
      orderData?.primary_client === CLIENT_TYPE.BROKER ? orderData?.broker : orderData?.shipper;
    setPrimaryClient(primaryClient || null);
  }, [open, orderData]);

  // CUSTOMERS INFO FOR TRIP OVERLAY
  useEffect(() => {
    if (!open || !tripData) return;

    const primaryClient =
      tripData.orders && tripData.orders[0]?.primary_client === CLIENT_TYPE.BROKER
        ? tripData.orders && tripData.orders[0]?.broker
        : tripData.orders && tripData.orders[0]?.shipper;
    setPrimaryClient(primaryClient || null);
  }, [open, tripData]);

  // on close overlay
  useEffect(() => {
    if (!open) {
      setDisplayTrips([]);
      setDisplayOrders([]);
      setMap(undefined);
      mapRef.current = null;
      setMarkers([]);
      setLiveMarkers([]);
      setCurrentTab(0);
    }
  }, [open]);

  useEffect(() => {
    if (!entityId || !entityType || !open) return;

    if (loadDetailReload && !!entityId && entityType === EntityType.ORDER) {
      refetchOrder();
      dispatch(setLoadDetailReload(false));
    } else if (tripDetailReload && !!entityId && entityType === EntityType.TRIP) {
      refetchTrip();
      dispatch(setTripDetailReload(false));
    }
  }, [loadDetailReload, tripDetailReload, entityId, entityType, open]);

  // AUTO SELECT TRIP TAB IN ORDER OVERLAY
  useEffect(() => {
    if (selectedTripId && displayTrips && isOrder) {
      const preselectedSegment = displayTrips.find((segment) => segment.id === selectedTripId);
      if (preselectedSegment) {
        const ix = isDriver
          ? displayTrips.indexOf(preselectedSegment)
          : displayTrips.indexOf(preselectedSegment) + 1;
        setCurrentTab(ix);
        onSelectedTripChange && onSelectedTripChange(selectedTripId);
        setTripIdSelected(selectedTripId);
        dispatch(setSelectedTripId(undefined));
      }
    }
  }, [selectedTripId, displayTrips, isOrder]);

  /**
   * A re-usable function to set map for given stops
   * */
  const setMapMarkers = (stops: TripStop[], startOrder?: number): Promise<void> => {
    return (async (): Promise<void> => {
      if (stops && stops.length <= MAX_STOP_MARKER_COUNT) {
        const markers: LocationMarker[] = getMapMarkers(
          stops[0]?.location || undefined,
          stops[stops.length - 1]?.location || undefined,
          stops
            .slice(1, -1)
            .filter((s) => !!s && !!s.location)
            .map((s) => s.location) as LoadLocation[],
          (startOrder ? startOrder : stops[0]?.sort_order + 1) ?? 1
        );

        if (markers.length >= 2) {
          setMarkers(markers);

          // start is first marker
          const start = markers[0];
          // end is last marker
          const end = markers[markers.length - 1];
          // stops are all markers except start and end
          const stops = markers.slice(1, markers.length - 1);

          // Render route
          const directionsRequest: google.maps.DirectionsRequest = {
            avoidFerries: true,
            origin: start.position,
            destination: end.position,
            travelMode: google.maps.TravelMode.DRIVING,
            unitSystem: google.maps.UnitSystem.IMPERIAL
          };

          if (stops.length > 0) {
            directionsRequest.waypoints = stops.map((s) => ({
              location: s.position,
              stopover: true
            }));
          }

          if (getAndRenderRoute) {
            try {
              // Use the lazy function instead of direct call
              await lazyGetAndRenderRoute(directionsRequest, !usesPcMiler);
            } catch (error) {
              console.error('DIRECTIONS RESULT ERROR', error);
              map?.setZoom(3);
            }
          }
        }
      }
    })();
  };

  const handleDrawerClose = (): void => {
    dispatch(setEntityOverlayInitialTabId(null));
    hasInitialTabChanged.current = false;
    onClose && onClose();
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
    clickFromTab: boolean
  ): void => {
    if (clickFromTab) {
      setCurrentTab(parseInt(newValue));

      if (!enableLtl && displayTrips.length > 0) {
        setTripIdSelected(displayTrips[parseInt(newValue)].id);
      }

      return;
    }

    let tabsToUse: any = [];
    if (isOrder && displayTrips.length > 0) {
      tabsToUse = displayTrips;
    } else if (isTrip && displayOrders.length > 0) {
      tabsToUse = displayOrders;
    }

    const tabToAdd = isOrder ? { id: orderData?.id } : { id: tripData?.id };

    isDriver ? (tabsToUse = [...tabsToUse, tabToAdd]) : (tabsToUse = [tabToAdd, ...tabsToUse]);

    const index = tabsToUse.findIndex((tab: any) => tab.id === newValue);
    setCurrentTab(index);
  };

  const openScanner = (): void => {
    setIsTwScannerOpen(true);
    // close BOL upload modal if scanner was opened from BOL upload
    setUploadBolOpen(false);
  };

  const showDriverAckConfirmationModal = useMemo(() => {
    if (!open) return false;
    if (!enableDriverAcknowledge) return false;
    if (!isDriver) return false;
    if (!isDriverAckSettingOn) return false;
    if (!tenantDriverSettings) return false;
    if (!enableLtl && !displayTrips) return false;
    if (!enableLtl && displayTrips.length < 1) return false;
    if (enableLtl && !tripData) return false;
    if (!selectedTripId && !tripIdSelected && !entityId) return false;

    const currentTrip = !enableLtl
      ? displayTrips.find((t) => t.id === tripIdSelected || selectedTripId)
      : tripData;

    if (!currentTrip) return false;

    const tripDriver = currentTrip.drivers.find((d) => d.id === userData.driver.id);

    if (!tripDriver) return false;

    // Company driver
    if (tripDriver.type === DRIVER_TYPE.COMPANY_DRIVER || !tripDriver.type) {
      // if company driver ack type setting is not CONFIRMATION, then don't show the modal
      if (tenantDriverSettings.company_driver_ack_type !== DriverAckType.CONFIRMATION) return false;

      const { driver_ack_status } = tripDriver;

      // if trip driver ack status is CONFIRMED, then don't show the modal
      if (driver_ack_status === TripDriverAckStatus.CONFIRMED) return false;

      (driver_ack_status === null || driver_ack_status === TripDriverAckStatus.SENT) &&
        setOpenDriverAckConfirmation(true);
    }

    // Owner operator driver
    if (tripDriver.type === DRIVER_TYPE.OWNER_OPERATOR_DRIVER) {
      // if owner operator driver ack type setting is not CONFIRMATION, then don't show the modal
      if (
        tenantDriverSettings.owner_operator_driver_ack_type !==
        OwnerOperatorDriverAckType.CONFIRMATION
      )
        return false;

      const { driver_ack_status } = tripDriver;

      // if trip driver ack status is CONFIRMED, then don't show the modal
      if (driver_ack_status === TripDriverAckStatus.CONFIRMED) return false;

      (driver_ack_status === null || driver_ack_status === TripDriverAckStatus.SENT) &&
        setOpenDriverAckConfirmation(true);
    }

    return true;
  }, [
    isDriver,
    entityType,
    tenantDriverSettings,
    isDriverAckSettingOn,
    JSON.stringify(displayTrips),
    open,
    selectedTripId,
    tripIdSelected,
    entityId,
    enableLtl,
    tripData,
    enableDriverAcknowledge
  ]);

  const showRejectedTrip = useMemo(() => {
    if (!open) return false;
    if (!enableDriverAcknowledge) return false;
    if (!isDriver) return false;
    if (!isDriverAckSettingOn) return false;
    if (!tenantDriverSettings) return false;
    if (!enableLtl && !displayTrips) return false;
    if (!enableLtl && displayTrips.length < 1) return false;
    if (enableLtl && !tripData) return false;
    if (!selectedTripId && !tripIdSelected && !entityId) return false;

    const currentTrip = !enableLtl
      ? displayTrips.find((t) => t.id === (enableLtl ? entityId : tripIdSelected || selectedTripId))
      : tripData;

    if (!currentTrip) return false;

    const tripDriver = currentTrip.drivers.find((d) => d.id === userData.driver.id);

    const isRejectedTrip = tripDriver?.driver_ack_status === TripDriverAckStatus.REJECTED;

    return isRejectedTrip;
  }, [
    tripData,
    enableLtl,
    entityId,
    tripIdSelected,
    selectedTripId,
    isDriver,
    displayTrips,
    open,
    isDriverAckSettingOn
  ]);

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={handleDrawerClose}
      PaperProps={{
        className: 'load-detail__main'
      }}
    >
      {showRejectedTrip ? (
        <TwRejectedTrip fromOverlay={true} onClose={handleDrawerClose} />
      ) : (
        <>
          <HeaderOverlay
            entityType={entityType || EntityType.ORDER}
            readableId={
              isOrder && orderData?.readable_id
                ? orderData?.readable_id
                : tripData?.readable_id || ''
            }
            client={primaryClient?.name || ''}
            currentTab={currentTab}
            loadStatus={orderData?.status}
            tripStatus={tripData?.status}
            ediLoadTenderId={orderData?.edi_load_tender_id}
            truck={truck}
            displaySegments={displayTrips}
            onClose={handleDrawerClose}
          />
          <StyledTabsContainer>
            <OverlayTabs
              displaySegments={isOrder && displayTrips.length > 0 ? displayTrips : []}
              displayOrders={isTrip && displayOrders.length > 0 ? displayOrders : []}
              entityTypeView={entityType}
              orderReadableId={orderData?.readable_id || ''}
              isFilteredView={isDriver}
              currentTab={currentTab}
              onTabChange={handleTabChange}
            />
          </StyledTabsContainer>
          {!enableHideGoogleMapsAndRoutingCalculations &&
            enableLiveTracking &&
            matchesMobileOrTablet && (
              <TwLiveTrackingAlert
                status={liveStatus}
                equipment={truck}
                entityType={liveTrackedEntity}
                timestamp={
                  truck?.latest_reading?.truckercloud_reading_timestamp
                    ? DateUtils.getFullLocaleDate(
                        truck?.latest_reading?.truckercloud_reading_timestamp
                      )
                    : undefined
                }
              />
            )}
          {googleMapsLoaded && showMap && !enableHideGoogleMapsAndRoutingCalculations && (
            <TwMap
              map={map}
              setMap={setMap}
              mapRef={mapRef}
              className="load-detail__map"
              center={DEFAULT_MAP_CENTER}
              zoom={2}
              disableDefaultUI
              mapTypeControl={enableLiveTracking ? true : false}
              mapTypeControlOptions={{
                mapTypeIds: ['roadmap', 'satellite', 'terrain'],
                position: google.maps.ControlPosition.TOP_LEFT,
                style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR
              }}
              showReloadButton={
                enableLiveTracking && !isDriverOrCarrier && liveMarkers && liveMarkers.length > 0
              }
              onReload={(): Promise<void> => {
                return new Promise((resolve) => {
                  refetchEquipment()
                    .catch(() => {
                      addNotification({
                        message: "Couldn't fetch new location. Please try again.",
                        variant: 'error'
                      });
                    })
                    .finally(() => {
                      resolve();
                    });
                });
              }}
            >
              {markers.map((marker, index) => (
                <TwMarker
                  key={marker.order || index}
                  map={map}
                  position={marker.position}
                  icon={getMarkerIcon(marker.type)}
                  label={{
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: '500',
                    text: marker?.order?.toString() || '',
                    fontFamily: 'Poppins'
                  }}
                />
              ))}

              {!isDriverOrCarrier &&
                liveMarkers.map((marker, index) => (
                  <TwMarker
                    key={markers.length + index + 1}
                    map={map}
                    position={marker.position}
                    icon={getMarkerIcon(marker.type)}
                    timestamp={marker.timestamp}
                    lastUpdatedTimestamp={marker.lastUpdatedTimestamp}
                    type={LocationMarkerType.LIVE_LOCATION}
                  />
                ))}
            </TwMap>
          )}

          {isOrder && orderData && displayTrips && (
            <TabPanel
              value={currentTab}
              key={isDriver ? displayTrips?.length : 0}
              index={isDriver ? displayTrips?.length || 1 : 0}
              id={`tw-order-detail-panel-${isDriver ? displayTrips?.length || 1 : 0}`}
            >
              <OrderTab
                order={orderData || null}
                primaryClient={primaryClient}
                readonly={readonly || isCustomerContact}
                forceOnlyEdit={forceOnlyEdit}
                reloadData={refetchOrder}
                changeTab={handleTabChange}
                handleMarkers={setMapMarkers}
              />
            </TabPanel>
          )}
          {isTrip && tripData && !isTripFetching && (
            <TabPanel
              value={currentTab}
              key={isDriver ? displayOrders?.length : 0}
              index={isDriver ? displayOrders?.length : 0}
              id={`tw-order-detail-panel-${isDriver ? displayOrders?.length || 1 : 0}`}
            >
              <TripTab
                segmentData={tripData || null}
                loadData={orderData || null}
                primaryClient={primaryClient}
                readonly={readonly || isCustomerContact}
                reloadData={refetchTrip}
                segmentOrder={tripData.sort_order}
                stops={stops}
                changeTab={handleTabChange}
                onStatusChange={onTripStatusChange}
                forceOnlyEdit={forceOnlyEdit}
                handleMarkers={setMapMarkers}
                open={open}
                onCloseDrawer={handleDrawerClose}
              />
            </TabPanel>
          )}
          {isOrder &&
            orderData &&
            displayTrips &&
            displayTrips.map((trip, index): JSX.Element => {
              return (
                <TabPanel
                  value={currentTab}
                  key={isDriver ? index : index + 1} // for non filtered view first tab is `full route tab` hence + 1
                  index={isDriver ? index : index + 1}
                  id={`tw-trip-detail-${isDriver ? index : index + 1}`}
                >
                  <TripTab
                    segmentData={{ ...trip, load_id: orderData.id }}
                    loadData={orderData || null}
                    primaryClient={primaryClient}
                    readonly={readonly || isCustomerContact}
                    reloadData={(entity, tripId): void => {
                      entity && entity === 'trip' && tripId ? refetchTrip() : refetchOrder();
                    }}
                    segmentOrder={trip.sort_order}
                    stops={stops}
                    changeTab={handleTabChange}
                    onStatusChange={onOrderStatusChange}
                    forceOnlyEdit={forceOnlyEdit}
                    handleMarkers={setMapMarkers}
                    open={open}
                    onCloseDrawer={handleDrawerClose}
                  />
                </TabPanel>
              );
            })}
          {isTrip &&
            tripData &&
            !isTripFetching &&
            displayOrders &&
            displayOrders.map((order, index): JSX.Element => {
              return (
                <TabPanel
                  value={currentTab}
                  key={isDriver ? index : index + 1} // for non filtered view first tab is `full route tab` hence + 1
                  index={isDriver ? index : index + 1}
                  id={`tw-trip-detail-${isDriver ? index : index + 1}`}
                >
                  <OrderTab
                    order={order || null}
                    primaryClient={primaryClient}
                    readonly={readonly || isCustomerContact}
                    forceOnlyEdit={forceOnlyEdit}
                    reloadData={(): void => {
                      entityType === EntityType.TRIP ? refetchTrip() : refetchOrder();
                    }}
                    changeTab={handleTabChange}
                    handleMarkers={setMapMarkers}
                  />
                </TabPanel>
              );
            })}

          {showDriverAckConfirmationModal && openDriverAckConfirmation && (
            <TwDriverAckConfirmationModal
              open={openDriverAckConfirmation}
              onClose={(): void => setOpenDriverAckConfirmation(false)}
              driverId={userData?.driver?.id as string}
              tripId={enableLtl ? entityId : tripIdSelected || selectedTripId}
              orderId={!enableLtl ? entityId : undefined}
            />
          )}

          <Backdrop
            className="load-detail__general-loader"
            sx={{
              color: '#fff',
              position: 'absolute',
              zIndex: 10000,
              top: 0,
              right: 0
            }}
            open={isOrderFetching || isTripFetching}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          {matchesMobileOrTablet && orderData && isRequireBolActive && isDriverOrCarrier && (
            <UploadBol
              open={uploadBolOpen}
              onClose={(): void => setUploadBolOpen(false)}
              loadId={orderData.id}
              onSuccess={refetchOrder}
              customUploadHandler={
                matchesMobileOrTablet && enableDynamicWebTwain ? openScanner : undefined
              }
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default TwEntityOverlay;
